import { ApiGateway } from '@/modules/api/api.gateway'
import { CheckEmailDto, UserCheckEmailDto } from '@/modules/user/core/schemas/check-email'
import { CreateCommentDto } from '@/modules/user/core/schemas/create-comment.schema'
import { CreateUserApplicationDto } from '@/modules/user/core/schemas/create-user-application.schema'
import { UpdatePersonalDataValues } from '@/modules/user/core/schemas/update-personal-data.schema'
import { UploadResumeDto } from '@/modules/user/core/schemas/upload-resume.schema'
import { IUserRepository } from '@/modules/user/core/user.repository.interface'
import {
  UserSearchResponse,
  User,
  UserPersonalData,
  PaginatedUserCommentResponse,
  UserApplicationsHistoryResponse,
  UserInterviewResponse,
  UserEmailsResponse,
} from '@/modules/user/core/user.types'
import { IdResponseDto } from '@/shared/domain/entities/common.entity'
import { PaginationDto } from '@/shared/domain/entities/pagination.entity'
import { UpdateJobPreferencesData } from '@/shared/domain/schemas/update-job-preferences.schema'
import { JobPreferences } from '@/types/application/applicant-info'

import { CreateTagsSchema } from './schemas/create-tags'

export class UserRepository implements IUserRepository {
  constructor(private api: ApiGateway) {}

  async search(query: string): Promise<UserSearchResponse> {
    return this.api.get<UserSearchResponse>(`/users/search?q=${query}`)
  }

  async get(id: string): Promise<User> {
    return this.api.get<User>(`/users/${id}`)
  }

  async getPersonalData(id: string): Promise<UserPersonalData> {
    return this.api.get<UserPersonalData>(`/users/personal-data/${id}`)
  }

  async updatePersonalData(id: string, data: UpdatePersonalDataValues): Promise<IdResponseDto> {
    return this.api.put<IdResponseDto, UpdatePersonalDataValues>(`/users/personal-data/${id}`, data)
  }

  async createApplication(data: CreateUserApplicationDto): Promise<IdResponseDto> {
    return this.api.post<IdResponseDto, CreateUserApplicationDto>('/users/application', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async checkEmail(data: CheckEmailDto): Promise<UserCheckEmailDto> {
    return this.api.post<UserCheckEmailDto, CheckEmailDto>('/users/check-email', data, {
      customErrorHandling: true,
    })
  }

  async uploadResume(data: FormData): Promise<UploadResumeDto> {
    return this.api.post<UploadResumeDto, FormData>('/users/resume', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async getUserComments(userId: string, pagination: PaginationDto): Promise<PaginatedUserCommentResponse> {
    return this.api.get<PaginatedUserCommentResponse>(`user-comments/${userId}`, {
      params: pagination,
    })
  }

  async createUserComment(userId: string, data: CreateCommentDto): Promise<IdResponseDto> {
    return this.api.post<IdResponseDto, CreateCommentDto>(`user-comments/${userId}`, data)
  }

  async updateJobPreferences(id: string, data: UpdateJobPreferencesData): Promise<JobPreferences> {
    return this.api.put<JobPreferences, UpdateJobPreferencesData>(`/users/${id}/job-preferences`, data)
  }

  async createTag(userId: string, data: CreateTagsSchema): Promise<IdResponseDto> {
    return this.api.post<IdResponseDto, CreateTagsSchema>(`/users/${userId}/tags`, data)
  }

  async getApplicationsHistory(userId: string): Promise<UserApplicationsHistoryResponse[]> {
    return this.api.get<UserApplicationsHistoryResponse[]>(`/users/${userId}/applications-history`)
  }

  async getInterviews(userId: string): Promise<UserInterviewResponse[]> {
    return this.api.get<UserInterviewResponse[]>(`/users/${userId}/interviews`)
  }

  async getEmails(userId: string): Promise<UserEmailsResponse[]> {
    return this.api.get<UserEmailsResponse[]>(`/users/${userId}/emails`)
  }
}
